import React, { useEffect, useState } from 'react';
import { bool, func, number, shape, string } from 'prop-types';
import * as R from 'ramda';
import { isValid } from 'date-fns/fp';
import { endOfDay, endOfTomorrow, format, startOfDay, startOfTomorrow } from 'date-fns';
import { useDispatch } from 'react-redux';
import {
  Alert,
  Box,
  Button,
  Checkbox,
  Divider,
  FormControlLabel,
  TextField,
  Typography,
} from '@mui/material';
import { DatePicker } from '@mui/x-date-pickers';
import CheckboxRoundedIcon from '@mui/icons-material/CheckBoxRounded';
import CheckBoxOutlineBlankRoundedIcon from '@mui/icons-material/CheckBoxOutlineBlankRounded';

// Components
import PowerSettingsNewRoundedIcon from '@mui/icons-material/PowerSettingsNewRounded';
import { DrawerLayoutWrapper, MDropdown, ProductIndicatorBadge } from 'common';
import SupplierAuthDialog from 'feature/loading-management/supplier-authorizations/confirmation-dialog/ConfirmationDialog.connector';
import { setHasUnsavedEditChanges } from 'feature/loading-management/supplier-authorizations/store/supplierAuthorizationSlice';

// utils
import {
  EXPIRATION_MESSAGE,
  NO_END_DATE,
  SUPPLIER_AUTHORIZATION_TYPE,
  SUPPLIER_AUTHORIZATION_TYPE_OPTIONS,
} from 'feature/loading-management/utils/const';
import {
  isLessThan,
  isNotEmpty,
  isNumber,
  validateIntGTZero,
  validateRequiredAutocomplete,
} from 'utils/helpers/formValidations';
import { formatNumber } from 'utils/helpers/format';
import { useTheme } from '@mui/material/styles';
import {
  alertOutlinedWarningStyles,
  alertRootStyles,
  calendarContentStyles,
  contentStyles,
  contentWrapperStyles,
  detailActiveStyles,
  detailBadgeStyles,
  detailDataCompanyStyles,
  detailLabelStyles,
  detailStyles,
  detailValueStyles,
} from './EditPanel.styles';

const notEquals = R.complement(R.equals);
const noneEmpty = R.complement(R.all(R.isEmpty));
const NO_END_DATE_ONLY = '2999-12-31';

const authNumberError = 'Authorization Number is required and must be between 4 and 6 digits.';

const validateAuthNumber = R.cond([
  [R.isEmpty, R.always(authNumberError)],
  [value => !isNumber(value), R.always(authNumberError)],
  [value => isLessThan(value, 3), R.always(authNumberError)],
  [value => !isLessThan(value, 6), R.always(authNumberError)],
  [R.T, R.always('')],
]);

const makeFooterButtonStyles = theme => ({
  marginLeft: theme.spacing(1),
  marginRight: theme.spacing(1),
  width: 135,
});

const ActiveIndicator = ({ active }) => (
  <Box
    alignItems="center"
    bgcolor={active ? 'primary.light' : 'surface.white'}
    border={active ? 0 : 1}
    borderColor="graphic.light"
    borderRadius="4px"
    color="primary.dark"
    display="flex"
    justifyContent="space-evenly"
    paddingBottom={0.5}
    paddingTop={0.5}
    width={60}
  >
    <PowerSettingsNewRoundedIcon sx={{ fontSize: 16 }} />
    <Typography component="span" variant="body2">
      <Box fontWeight={400}>{active ? 'On' : 'Off'}</Box>
    </Typography>
  </Box>
);
ActiveIndicator.propTypes = { active: bool };
ActiveIndicator.defaultProps = { active: false };

const EditPanel = props => {
  const dispatch = useDispatch();
  const {
    details,
    dialogDetails,
    editSupplierAuthorizationDetails,
    fetchDetails,
    onClose,
    productAuthorizationId,
    setDialogDetails,
    setForceCloseEditDrawer,
    toggleSupplierAuthActivation,
    viewBy,
  } = props;

  const {
    active,
    command,
    dataCompany,
    inventoryOwner,
    location,
    product,
    remainingVolume,
    supplier,
  } = details;
  const { message, params } = dialogDetails || {};
  const [authorizationNumber, setAuthorizationNumber] = useState('');
  const [authorizationVolume, setAuthorizationVolume] = useState('');
  const [endDate, setEndDate] = useState(NO_END_DATE);
  const [noEndDate, setNoEndDate] = useState(false);
  const [renewalVolume, setRenewalVolume] = useState('');
  const [type, setType] = useState(SUPPLIER_AUTHORIZATION_TYPE.UNLIMITED);
  const [validation, setValidation] = useState({
    authorizationNumber: '',
    authorizationVolume: '',
    endDate: '',
    renewalVolume: '',
    type: '',
  });
  const theme = useTheme();

  // Max allowed date (Dec 31, 2999)
  const MAX_ALLOWED_DATE = new Date(2999, 11, 31);

  const isLimited = R.identical(SUPPLIER_AUTHORIZATION_TYPE.LIMITED);
  const isUnlimited = R.identical(SUPPLIER_AUTHORIZATION_TYPE.UNLIMITED);

  const parseEndDateInput = value => {
    if (!value) return new Date(2999, 11, 31);
    if (value instanceof Date) return value;

    const valueAsString = String(value).trim();
    if (!valueAsString) return new Date(2999, 11, 31);

    if (valueAsString.includes('/')) {
      const [month, day, year] = valueAsString.split('/').map(Number);
      return new Date(year, month - 1, day);
    }

    const dateOnly = valueAsString.includes('T') ? valueAsString.split('T')[0] : valueAsString;
    const [year, month, day] = dateOnly.split('-').map(Number);
    return new Date(year, month - 1, day);
  };

  const getEndDateValidationMessage = value => {
    if (R.isNil(value)) return 'Invalid date';

    const parsedDate = value instanceof Date ? value : parseEndDateInput(value);

    if (!isValid(parsedDate)) return 'Invalid date';
    if (parsedDate <= startOfDay(new Date())) return 'Invalid date';
    if (parsedDate > endOfDay(MAX_ALLOWED_DATE)) return 'Date must be <= 12/31/2999';
    return '';
  };

  useEffect(() => {
    const parsedEndDate = parseEndDateInput(command.endDate);
    const normalizedEndDate = isValid(parsedEndDate) ? parsedEndDate : new Date(2999, 11, 31);
    const isNoEndDate = format(normalizedEndDate, 'yyyy-MM-dd') === NO_END_DATE_ONLY;

    setEndDate(normalizedEndDate);
    setNoEndDate(isNoEndDate);
  }, [command.endDate]);

  useEffect(() => {
    setAuthorizationNumber(R.defaultTo('', command.authorizationNumber));
    setValidation(R.assoc('authorizationNumber', validateAuthNumber(command.authorizationNumber)));
  }, [command.authorizationNumber]);

  useEffect(() => {
    const initialAuthVol = R.defaultTo('', command.authorizationVolume);
    setAuthorizationVolume(R.isEmpty(initialAuthVol) ? '' : String(initialAuthVol));
  }, [command.authorizationVolume]);

  useEffect(() => {
    const initialRenewVol = R.defaultTo('', command.renewalVolume);
    setRenewalVolume(R.isEmpty(initialRenewVol) ? '' : String(initialRenewVol));
  }, [command.renewalVolume]);

  useEffect(() => {
    setType(R.defaultTo(SUPPLIER_AUTHORIZATION_TYPE.UNLIMITED, command.type));
  }, [command.type]);

  useEffect(() => {
    fetchDetails(productAuthorizationId);
    return () => setForceCloseEditDrawer(false);
  }, [fetchDetails, productAuthorizationId, setForceCloseEditDrawer]);

  const handleAuthorizationNumberChange = R.compose(
    value => setValidation(R.assoc('authorizationNumber', value)),
    validateAuthNumber,
    R.tap(setAuthorizationNumber),
    R.path(['target', 'value'])
  );

  const handleAuthorizationVolumeChange = R.compose(
    value => setValidation(R.assoc('authorizationVolume', value)),
    validateIntGTZero,
    R.tap(setAuthorizationVolume),
    R.path(['target', 'value'])
  );

  const handleEndDateError = reason => {
    const endDateValidationMessage = R.isNil(reason) ? '' : getEndDateValidationMessage(endDate);

    setValidation(prevValidation =>
      prevValidation.endDate === endDateValidationMessage
        ? prevValidation
        : R.assoc('endDate', endDateValidationMessage, prevValidation)
    );
  };

  const handleNoEndDateChange = R.compose(
    R.ifElse(
      R.identity,
      R.tap(R.o(setEndDate, R.always(NO_END_DATE))),
      R.tap(R.o(setEndDate, R.always(endOfTomorrow())))
    ),
    noEndDateParam => {
      setValidation(R.assoc('endDate', ''));
      return noEndDateParam;
    },
    R.tap(setNoEndDate),
    R.nthArg(1)
  );

  const handleEndDateChange = date => {
    setEndDate(date);
    const endDateValidationMessage = getEndDateValidationMessage(date);

    setValidation(prevValidation => R.assoc('endDate', endDateValidationMessage, prevValidation));
  };

  const handleTypeChange = R.compose(
    val => setValidation(R.assoc('type', val)),
    validateRequiredAutocomplete,
    R.tap(setType),
    R.prop('value')
  );

  const handleRenewalVolumeChange = R.compose(
    value => setValidation(R.assoc('renewalVolume', value)),
    validateIntGTZero,
    R.tap(setRenewalVolume),
    R.path(['target', 'value'])
  );

  const handleSubmit = () => {
    // alwaysUndefined :: a -> undefined
    const alwaysUndefined = () => undefined;
    const submitEndDate =
      noEndDate || !isValid(endDate) ? NO_END_DATE_ONLY : format(endDate, 'yyyy-MM-dd');

    const transformations = {
      authorizationVolume: R.ifElse(() => isUnlimited(type), alwaysUndefined, Number),
      renewalVolume: R.ifElse(
        R.either(
          () => isLimited(type),
          () => isUnlimited(type)
        ),
        alwaysUndefined,
        Number
      ),
    };

    setDialogDetails({
      confirmAction: editSupplierAuthorizationDetails,
      header: 'Edit supplier authorization?',
      message: 'Would you like to save your changes to supplier authorization?',
      params: R.evolve(transformations, {
        authorizationNumber,
        authorizationVolume,
        endDate: submitEndDate,
        productAuthorizationId,
        renewalVolume,
        type,
        viewBy,
      }),
    });
  };

  const handleToggleAuthorization = () => {
    const locationName = location.nameShort;
    const productName = product.nameShort;
    const supplierName = supplier.nameShort;

    setDialogDetails({
      confirmAction: toggleSupplierAuthActivation,
      header: `Turn ${active ? 'Off' : 'On'} Supplier Authorization?`,
      message: `Turn ${
        active ? 'off' : 'on'
      } ${productName} loading for ${supplierName} at ${locationName}? Supplier Authorizations can be turned back ${
        active ? 'on' : 'off'
      } at any time.`,
      params: { active, locationName, productAuthorizationId, productName, supplierName },
    });
  };

  // We only care about certain validations,
  // depending on selected 'type'
  const validationsThatMatter = R.cond([
    [
      R.equals(SUPPLIER_AUTHORIZATION_TYPE.UNLIMITED),
      R.flip(R.omit(['authorizationVolume', 'renewalVolume'])),
    ],
    // If LIMITED then don't care about renewalVolume
    // but validate the authorizationVolume
    [
      R.equals(SUPPLIER_AUTHORIZATION_TYPE.LIMITED),
      R.compose(
        R.mergeLeft({ authorizationVolume: validateIntGTZero(authorizationVolume) }),
        R.flip(R.dissoc('renewalVolume'))
      ),
    ],
    [
      R.T,
      R.flip(
        R.mergeLeft({
          authorizationVolume: validateIntGTZero(authorizationVolume),
          renewalVolume: validateIntGTZero(renewalVolume),
        })
      ),
    ],
  ])(type, validation);

  // Include end date validation when noEndDate is false
  const endDateMessage = noEndDate ? '' : getEndDateValidationMessage(endDate);

  const finalValidations = noEndDate
    ? R.omit(['endDate'], validationsThatMatter)
    : R.mergeLeft({ endDate: validation.endDate || endDateMessage }, validationsThatMatter);

  const authNumValidation = R.cond([
    [value => !isNumber(value), R.F],
    [value => isLessThan(value, 3), R.F],
    [value => !isLessThan(value, 6), R.F],
    [R.T, R.T],
  ])(authorizationNumber);

  // Build a normalized data of the current state values that affect submission
  const normalizeVolume = v => (R.isNil(v) || v === '' ? '' : String(Number(v)));

  const normalizeEndDateComparable = value => {
    if (R.isNil(value) || value === '') return '';

    let parsedDate;
    if (value instanceof Date) {
      parsedDate = value;
    } else {
      const dateOnly = String(value).split('T')[0].trim();
      if (!dateOnly) return '';
      const parts = dateOnly.split('-').map(Number);
      parsedDate = new Date(parts[0], parts[1] - 1, parts[2]);
    }

    if (!isValid(parsedDate)) return '';

    const dateOnly = format(parsedDate, 'yyyy-MM-dd');
    return dateOnly === NO_END_DATE_ONLY ? 'NO_END_DATE' : dateOnly;
  };

  const getCurrentData = () => ({
    authorizationNumber,
    authorizationVolume: normalizeVolume(authorizationVolume),
    endDateComparable: noEndDate ? 'NO_END_DATE' : normalizeEndDateComparable(endDate),

    renewalVolume: normalizeVolume(renewalVolume),

    type,
  });

  const getInitialData = () => ({
    authorizationNumber: R.defaultTo('', command.authorizationNumber),
    authorizationVolume: normalizeVolume(command.authorizationVolume),
    endDateComparable: normalizeEndDateComparable(command.endDate),
    renewalVolume: normalizeVolume(command.renewalVolume),
    type: R.defaultTo(SUPPLIER_AUTHORIZATION_TYPE.UNLIMITED, command.type),
  });

  const hasChanges = notEquals(getInitialData(), getCurrentData());

  const submitDisabled = noneEmpty(R.values(finalValidations)) || !authNumValidation || !hasChanges;

  useEffect(() => {
    dispatch(setHasUnsavedEditChanges(hasChanges));
    return () => dispatch(setHasUnsavedEditChanges(false));
  }, [dispatch, hasChanges]);

  const footerButtons = (
    <>
      <Button onClick={handleToggleAuthorization} sx={makeFooterButtonStyles} variant="outlined">
        Turn Off
      </Button>
      <Button disabled={submitDisabled} onClick={handleSubmit} sx={makeFooterButtonStyles}>
        Submit
      </Button>
    </>
  );

  return (
    <DrawerLayoutWrapper
      buttons={footerButtons}
      headerButtonText="CLOSE"
      isLoading={false}
      onHeaderButtonClick={onClose}
      title="Edit Supplier Authorization"
    >
      <Box sx={{ ...contentWrapperStyles }}>
        <Box sx={{ ...contentStyles(theme) }}>
          <Box sx={{ ...detailStyles }}>
            <Typography sx={{ ...detailLabelStyles(theme) }} variant="caption">
              Inventory Owner
            </Typography>
            <Typography sx={{ ...detailValueStyles(theme) }} variant="body1">
              {inventoryOwner.nameShort}
            </Typography>
            <Box sx={{ ...detailActiveStyles }}>
              <ActiveIndicator active={active} />
            </Box>
          </Box>
          <Box sx={{ ...detailStyles }}>
            <Typography sx={{ ...detailLabelStyles(theme) }} variant="caption">
              Supplier
            </Typography>
            <Typography sx={{ ...detailValueStyles(theme) }} variant="body1">
              {supplier.nameShort}
            </Typography>
            <Typography sx={{ ...detailDataCompanyStyles }} variant="body2">
              {dataCompany}
            </Typography>
          </Box>

          <Divider />

          <Box sx={{ ...detailStyles }}>
            <Typography sx={{ ...detailLabelStyles(theme) }} variant="caption">
              Location
            </Typography>
            <Typography sx={{ ...detailValueStyles(theme) }} variant="body1">
              {location.nameShort}
            </Typography>
          </Box>
          <Box sx={{ ...detailStyles }}>
            <Typography sx={{ ...detailLabelStyles(theme) }} variant="caption">
              Product
            </Typography>
            <Typography sx={{ ...detailValueStyles(theme) }} variant="body1">
              {product.nameShort}
            </Typography>

            <Box sx={{ ...detailBadgeStyles }}>
              <ProductIndicatorBadge proprietary={product.proprietary} />
            </Box>
          </Box>

          <Box sx={{ ...detailStyles }}>
            <Typography sx={{ ...detailLabelStyles(theme) }} variant="caption">
              Remaining Volume
            </Typography>
            <Typography sx={{ ...detailValueStyles(theme) }} variant="body1">
              {`${formatNumber(remainingVolume) || 0} Gallons`}
            </Typography>
          </Box>
          <Divider />

          <FormControlLabel
            control={
              <Checkbox
                checked={noEndDate}
                checkedIcon={<CheckboxRoundedIcon sx={{ color: 'secondary.main' }} />}
                icon={<CheckBoxOutlineBlankRoundedIcon />}
                name="noEndDate"
                onChange={handleNoEndDateChange}
                sx={{
                  '&.MuiCheckbox-root': {
                    borderRadius: '50%',
                    color: 'text.secondary',
                  },
                }}
              />
            }
            label="No End Date"
            sx={{ width: 'fit-content' }}
          />

          {R.not(noEndDate) && (
            <Box sx={{ ...calendarContentStyles(theme) }}>
              <DatePicker
                disablePast
                error={isNotEmpty(validation.endDate)}
                id="end-date"
                label="End Date"
                maxDate={MAX_ALLOWED_DATE}
                minDate={startOfTomorrow()}
                onChange={handleEndDateChange}
                onError={handleEndDateError}
                slotProps={{
                  textField: {
                    error: isNotEmpty(validation.endDate),
                    helperText: validation.endDate,
                  },
                }}
                value={endDate}
              />
              <Alert
                // If this alert style is used elsewhere, consider moving these style overrides to theme
                icon={false}
                severity="warning"
                sx={{ ...alertOutlinedWarningStyles(theme), ...alertRootStyles(theme) }}
                variant="outlined"
              >
                {EXPIRATION_MESSAGE}
              </Alert>
            </Box>
          )}

          <TextField
            data-testid="authorization-number"
            error={isNotEmpty(validation.authorizationNumber)}
            helperText={validation.authorizationNumber}
            label="Authorization Number"
            onChange={handleAuthorizationNumberChange}
            value={authorizationNumber}
          />
          <MDropdown
            error={isNotEmpty(validation.type)}
            helperText={validation.type}
            id="type"
            items={SUPPLIER_AUTHORIZATION_TYPE_OPTIONS}
            label="Type"
            name="type"
            onChange={handleTypeChange}
            value={type}
          />

          {R.not(R.equals(type, SUPPLIER_AUTHORIZATION_TYPE.UNLIMITED)) && (
            <>
              <TextField
                error={isNotEmpty(validation.authorizationVolume)}
                helperText={validation.authorizationVolume}
                label="Authorization Volume (Gallons)"
                onChange={handleAuthorizationVolumeChange}
                value={authorizationVolume}
              />
              {R.not(R.equals(type, SUPPLIER_AUTHORIZATION_TYPE.LIMITED)) && (
                <TextField
                  error={isNotEmpty(validation.renewalVolume)}
                  helperText={validation.renewalVolume}
                  id="renewalVolume"
                  label="Renewal Volume (Gallons)"
                  onChange={handleRenewalVolumeChange}
                  value={renewalVolume}
                />
              )}
            </>
          )}
        </Box>
        {!R.isNil(params) && message && <SupplierAuthDialog />}
      </Box>
    </DrawerLayoutWrapper>
  );
};

EditPanel.propTypes = {
  details: shape({
    active: bool,
    command: shape({
      authorizationNumber: string,
      authorizationVolume: number,
      endDate: string,
      remainingVolume: number,
      renewalVolume: number,
      type: string,
    }),
    dataCompany: string,
    inventoryOwner: shape({
      nameShort: string,
    }),
    location: shape({
      id: number,
      nameShort: string,
    }),
    product: shape({
      nameShort: string,
      proprietary: string,
    }),
    supplier: shape({
      idName: string,
      nameShort: string,
    }),
  }).isRequired,
  dialogDetails: shape({
    message: string,
    params: shape({}),
  }),
  editSupplierAuthorizationDetails: func.isRequired,
  fetchDetails: func.isRequired,
  onClose: func.isRequired,
  productAuthorizationId: number.isRequired,
  setDialogDetails: func.isRequired,
  setForceCloseEditDrawer: func.isRequired,
  toggleSupplierAuthActivation: func.isRequired,
  viewBy: string,
};

EditPanel.defaultProps = {
  dialogDetails: undefined,
  viewBy: undefined,
};

export default EditPanel;
